import { useState } from "react";
import { linkWithPopup, signInWithPopup } from "firebase/auth";
import {
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import {
  auth,
  db,
  googleProvider,
} from "../../../firebase.js";
import { ONBOARDING_STATUS_NEEDS_PROFILE } from "../userOnboardingStatus.js";
import {
  captureGuestTreeForMigration,
  isGuestUser,
} from "../guestSession.js";
import { GoogleIcon } from "./AuthIcons";
import socialStyles from "./SocialAuth.module.css";
import pageStyles from "../styles/AuthPage.module.css";

export default function SocialAuth({
  onSuccess,
  onError,
  variant = "inline",
}) {
  const [loading, setLoading] = useState(false);

  /**
   * A guest links the provider so the trial giftees stay on the same uid. When the
   * provider account already exists, the trial is snapshotted and re-created under
   * it after sign-in (see `flushPendingGuestTree` in the auth screen).
   */
  const authenticateWithProvider = async (provider) => {
    const guestUser = isGuestUser(auth.currentUser) ? auth.currentUser : null;
    if (!guestUser) {
      return signInWithPopup(auth, provider);
    }

    try {
      return await linkWithPopup(guestUser, provider);
    } catch (error) {
      // Firebase reports an account owned by someone else either way.
      const alreadyOnAnotherAccount =
        error.code === "auth/credential-already-in-use" ||
        error.code === "auth/account-exists-with-different-credential";
      if (!alreadyOnAnotherAccount) throw error;
      await captureGuestTreeForMigration();
      return signInWithPopup(auth, provider);
    }
  };

  const handleSocialAuth = async (provider, providerName) => {
    setLoading(true);
    try {
      const result = await authenticateWithProvider(provider);
      const user = result.user;

      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const isNewUser = !userDocSnap.exists();
      const phoneAlreadyVerified =
        !isNewUser && userDocSnap.data().phoneVerified === true;

      const providerFlags = {
        google: providerName === "Google",
        email: false,
        phone: false,
      };

      if (isNewUser) {
        await setDoc(userDocRef, {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          phoneNumber: null,
          authProviders: providerFlags,
          phoneVerified: false,
          onboardingStatus: ONBOARDING_STATUS_NEEDS_PROFILE,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastSignInAt: serverTimestamp(),
        });
      } else {
        await setDoc(
          userDocRef,
          {
            lastSignInAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      const needsPhoneVerification = !phoneAlreadyVerified;
      onSuccess && onSuccess(needsPhoneVerification);
    } catch (error) {
      console.error(`${providerName} auth error:`, error);
      onError && onError(error.message || `${providerName} sign-in failed`);
    } finally {
      setLoading(false);
    }
  };

  if (variant === "start") {
    return (
      <button
        type="button"
        className={pageStyles.startProviderBtn}
        onClick={() => handleSocialAuth(googleProvider, "Google")}
        disabled={loading}
      >
        <GoogleIcon />
        {loading ? "Signing in\u2026" : "Continue with Google"}
      </button>
    );
  }

  return (
    <div className={socialStyles.socialAuth}>
      <div className={socialStyles.divider}>
        <span>or</span>
      </div>
      <div className={socialStyles.socialButtons}>
        <button
          type="button"
          className={socialStyles.socialButton}
          onClick={() => handleSocialAuth(googleProvider, "Google")}
          disabled={loading}
        >
          <GoogleIcon />
          {loading ? "Signing in\u2026" : "Continue with Google"}
        </button>
      </div>
    </div>
  );
}
